import { Box, Grid, GridItem, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { ActiveStepLabels } from 'containers/Esrs/EsrsUtilComponents';
import { DatapointBrowserBox } from 'containers/Esrs/pieces/Assessment/DatapointBrowser/DatapointBrowserBox/DatapointBrowserBox';
import { Modal } from 'Molecules';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { SearchIcon } from 'Tokens/Icons/Function';
import { MFDatapointsBrowserTable } from './MFDatapointsBrowserTable';
import { GetDrMaterialMetricsQuery_, useDeleteMaterialMetricsMutation } from 'models';
import { MfDisclosureRequirementTableMetric } from './DrMateriality.hooks';
import {
  MappedDisclosureRequirements,
  Materiality,
  MaterialityState,
  MetricMaterialityState,
  ParentMaterialMetrics,
} from '../DoubleMaterialityAssessment.d';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';
import { MaterialMetric, Metric } from '../../MaterialityAssessment.d';
import { MetricsMaterialityTable } from './MetricsMaterialityTable';
import { Button, TruncatableText } from 'Atoms';
import { useParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import { MFDatapointsDRBrowserViewHistory } from './MFDatapointsDRBrowserViewHistory';
import { useKnowledgeBase } from 'Features';
import { MetricsMaterialityTableMetric } from './DrMateriality';

export const MFDatapointsDRBrowser = ({
  tableData,
  materialMetrics,
  isCollectOnly,
  isDrAssessed,
  readOnly,
  parentMetrics,
  parentMateriality,
  setMaterialMetrics,
  drRef,
  disclosureRequirement,
  isGroup,
  hasParent,
  drMateriality,
  selectedMetricsToUpdate,
  setSelectedMetricsToUpdate,
  handleUpdateMateriality,
}: {
  tableData: MfDisclosureRequirementTableMetric[];
  materialMetrics: MaterialMetric[];
  isCollectOnly: boolean;
  isDrAssessed: boolean;
  readOnly: boolean;
  parentMetrics: ParentMaterialMetrics;
  parentMateriality?: Materiality | MaterialityState;
  setMaterialMetrics: Dispatch<SetStateAction<GetDrMaterialMetricsQuery_['esrs_MaterialMetric']>>;
  drRef: string;
  disclosureRequirement?: MappedDisclosureRequirements[number];
  isGroup: boolean;
  hasParent: boolean;
  drMateriality: MaterialityState | null;
  selectedMetricsToUpdate: MetricsMaterialityTableMetric[];
  setSelectedMetricsToUpdate: Dispatch<SetStateAction<MetricsMaterialityTableMetric[]>>;
  handleUpdateMateriality: (
    selection: MetricMaterialityState,
    metric: Metric,
    isChild?: boolean
  ) => void;
}) => {
  const { framework = '' } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onOpen: onKnowledgeBaseOpen } = useKnowledgeBase();

  const [viewHistory, setViewHistory] = useState<string[]>([disclosureRequirement?.title ?? '']);
  const [materialMetricsToDelete, setMaterialMetricsToDelete] = useState<string[]>([]);
  const [deleteMaterialMetrics] = useDeleteMaterialMetricsMutation();

  const otherMetrics = useMemo(
    () =>
      materialMetrics.filter(
        (mm) => mm.metric?.disclosureRequirementRef !== drRef && mm.metric?.isAssessable
      ),
    [materialMetrics, drRef]
  );
  const hasOtherMetrics = useMemo(() => !!otherMetrics.length, [otherMetrics]);

  const hasPhasedInOption =
    disclosureRequirement?.additionalTypes?.some((additionalType) =>
      additionalType?.title?.includes('Phased')
    ) ?? false;

  const handleConfirm = () => {
    if (!!materialMetricsToDelete.length) {
      deleteMaterialMetrics({ variables: { ids: materialMetricsToDelete } }).then(() => {
        setMaterialMetricsToDelete([]);
      });
    }
    onClose();
  };

  const subCategoryMap = useMemo(() => {
    const subCategoryMap: {
      [key: string]: { name: string; metrics: MfDisclosureRequirementTableMetric[] };
    } = {};
    tableData?.map((metric) => {
      if (subCategoryMap.hasOwnProperty(metric.subCategoryReference)) {
        subCategoryMap[metric.subCategoryReference].metrics.push(metric);
      } else {
        subCategoryMap[metric.subCategoryReference] = {
          name: metric.subCategory,
          metrics: [metric],
        };
      }
    });

    return subCategoryMap;
  }, [tableData]);

  return tableData.length ? (
    <Box
      pl={hasOtherMetrics ? '0px' : '32px'}
      ml={hasOtherMetrics ? '0px' : '16px'}
      borderColor="border.hover"
      pb="24px"
      alignItems="stretch"
      gap="8px"
    >
      {!hasOtherMetrics && (
        <ActiveStepLabels
          title="Add other datapoints"
          hasSteps={false}
          ml="70px"
          clickable={false}
        />
      )}

      {hasOtherMetrics ? (
        <VStack alignItems="start" gap="8px">
          <MetricsMaterialityTable
            metrics={tableData
              .filter((data) => otherMetrics.some((met) => met.metricRef === data?.reference))
              .map((data) => data.metric)}
            readOnly={readOnly}
            disclosureRequirement={disclosureRequirement}
            drMateriality={drMateriality}
            materialMetrics={materialMetrics}
            hasParent={hasParent}
            parentMetrics={parentMetrics}
            isCollectOnly={isCollectOnly}
            isGroup={isGroup}
            isOtherMetrics
            selectedMetricsToUpdate={selectedMetricsToUpdate}
            setSelectedMetricsToUpdate={setSelectedMetricsToUpdate}
            handleUpdateMateriality={handleUpdateMateriality}
          />
          <Button
            variant="secondary"
            leftIcon={<SearchIcon color="text.selected" />}
            ml="46px"
            onClick={onOpen}
          >
            Add more
          </Button>
        </VStack>
      ) : (
        <DatapointBrowserBox
          onClickBrowse={onOpen}
          onClickLearnMore={() => onKnowledgeBaseOpen('mf-data-points-database')}
        />
      )}
      <Modal
        customHeader={
          viewHistory.length > 1 ? (
            <MFDatapointsDRBrowserViewHistory
              viewHistory={viewHistory}
              setViewHistory={setViewHistory}
              subCategoryMap={subCategoryMap}
            />
          ) : (
            <Typography alignSelf={'center'}>{viewHistory[0]}</Typography>
          )
        }
        size="xl"
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirm}
        marginY="32px"
        contentProps={{ w: '75%', maxW: '1800px', minW: '1200px' }}
      >
        <VStack gap={'32px'}>
          {viewHistory.length === 1 && Object.keys(subCategoryMap).length > 1 && (
            <Grid templateColumns="repeat(3, 1fr)" gap="8px" width={'100%'}>
              {Object.keys(subCategoryMap)?.map((subCategory) => (
                <GridItem>
                  <HStack
                    padding={'16px'}
                    gap={'8px'}
                    borderRadius={'10px'}
                    shadow="interactive"
                    bg="bg.elevated.interactive"
                    _hover={{
                      shadow: 'interactive.hover',
                      background: 'bg.elevated.interactive.hover',
                    }}
                    cursor="pointer"
                    justifyContent={'space-between'}
                    height={'52px'}
                    onClick={() => {
                      setViewHistory([...viewHistory, subCategory]);
                    }}
                  >
                    <TruncatableText
                      fontSize={'14px'}
                      fontWeight={'600'}
                      text={subCategoryMap[subCategory].name}
                    />
                    <Typography
                      textAlign={'right'}
                      fontSize={'14px'}
                      fontWeight={'400'}
                      color={'text.hint'}
                    >
                      {subCategoryMap[subCategory].metrics.length}
                    </Typography>
                  </HStack>
                </GridItem>
              ))}
            </Grid>
          )}
          <MFDatapointsBrowserTable
            mfData={viewHistory.length === 1 ? tableData : subCategoryMap[viewHistory[1]].metrics}
            materialMetrics={materialMetrics}
            isCollectOnly={isCollectOnly}
            isDrAssessed={isDrAssessed}
            parentMetrics={parentMetrics}
            parentMateriality={parentMateriality}
            framework={framework as FrameworkRefs}
            hasPhasedInOption={hasPhasedInOption}
            disclosureRequirement={disclosureRequirement}
            setMaterialMetrics={setMaterialMetrics}
            setMaterialMetricsToDelete={setMaterialMetricsToDelete}
          />
        </VStack>
      </Modal>
    </Box>
  ) : (
    <></>
  );
};
